/**
 * Animated 3D keyword sphere (the "球在动" word cloud).
 *
 * A single canonical renderer shared by the topic landing page's two
 * paint paths (navigationController warm-cache + topicDetailHydrator
 * async). Previously those two paths fought each other with mismatched
 * CSS variables, so the cloud collapsed into an unstyled pile at the
 * center. This module owns the layout + animation so both callers get
 * the same rotating glass-pill sphere.
 *
 * Behaviour:
 *   - Stored keywords render in their original order and spelling.
 *   - Constant container height; the sphere rotates inside it.
 *   - Font size scales with the keyword coefficient (bigger weight =
 *     bigger word).
 *   - A requestAnimationFrame loop rotates the sphere; depth controls
 *     scale / opacity / z-index for the 3D illusion.
 *   - Idempotent: re-rendering with the same keyword set is a no-op,
 *     which keeps the warm-cache → hydrated double paint flicker-free.
 *   - Honors prefers-reduced-motion (renders a static sphere).
 */

import { keywordWeightCssValue } from '../../shared/keywordWeight';

export interface KeywordItem {
  keyword: string;
  coefficient: number;
}

interface CloudNode {
  el: HTMLElement;
  x: number;
  y: number;
  z: number;
}

interface CloudHandle {
  raf: number | null;
  signature: string;
  firstEl: HTMLElement | null;
  angleY: number;
  angleX: number;
  suspend: () => void;
  resume: () => void;
  cleanup: () => void;
}

const handles = new WeakMap<HTMLElement, CloudHandle>();

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const ROTATE_Y_PER_FRAME = 0.0045;
const ROTATE_X_PER_FRAME = 0.0018;
const INITIAL_ANGLE_X = -0.32;

export function renderKeywordCloud(container: HTMLElement, keywords: KeywordItem[]): void {
  const items = normalizeKeywords(keywords);
  const signature = items.map((item) => `${item.keyword}:${item.coefficient}`).join("|");

  const previous = handles.get(container);
  if (previous && previous.signature === signature && previous.firstEl?.parentElement === container) {
    // Same keyword set already rendered AND still in the DOM — leave
    // it running so the hydrated paint does not restart / flash the
    // cloud. (If a loading placeholder replaced our nodes, firstEl is
    // detached and we fall through to a rebuild.)
    return;
  }
  const initialAngleY = previous?.angleY ?? 0;
  const initialAngleX = previous?.angleX ?? INITIAL_ANGLE_X;
  previous?.cleanup();

  if (items.length === 0) {
    container.replaceChildren();
    return;
  }

  container.classList.remove("topic-keyword-cloud--static", "kw-cloud--skeleton");
  container.classList.add("kw-cloud-3d");

  const nodes: CloudNode[] = items.map((item, index) => {
    const el = document.createElement("span");
    el.className = "topic-keyword";
    el.textContent = item.keyword;
    el.style.setProperty("--kw-weight", keywordWeightCssValue(item.coefficient));
    // Fibonacci-sphere placement for an even distribution.
    // Open interval (index+0.5)/n avoids y=±1 at the poles, which
    // would zero out r and pin those words motionless on the Y axis.
    const y = 1 - ((index + 0.5) / items.length) * 2;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = GOLDEN_ANGLE * index;
    return { el, x: Math.cos(theta) * r, y, z: Math.sin(theta) * r };
  });

  container.replaceChildren(...nodes.map((n) => n.el));

  const reduceMotion = typeof window !== "undefined"
    && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  let radius = computeRadius(container);
  let angleY = initialAngleY;
  let angleX = initialAngleX;

  const project = () => {
    if (radius === 0) radius = computeRadius(container);
    projectNodes(nodes, radius, angleY, angleX);
  };

  project();

  const handle: CloudHandle = {
    raf: null,
    signature,
    firstEl: nodes[0]?.el ?? null,
    angleY,
    angleX,
    suspend: () => {},
    resume: () => {},
    cleanup: () => {},
  };

  const resume = () => {
    if (reduceMotion || handle.raf !== null) return;
    if (!container.isConnected || nodes[0]?.el.parentElement !== container) return;
    const tick = () => {
      // Stop when the container is gone OR its children were swapped
      // out from under us (e.g. paintTopicLoading replaced the cloud
      // with a loading placeholder for a different topic).
      if (!container.isConnected || nodes[0]?.el.parentElement !== container) {
        handle.raf = null;
        return;
      }
      angleY += ROTATE_Y_PER_FRAME;
      angleX += ROTATE_X_PER_FRAME;
      handle.angleY = angleY;
      handle.angleX = angleX;
      project();
      handle.raf = requestAnimationFrame(tick);
    };
    handle.raf = requestAnimationFrame(tick);
  };
  handle.suspend = () => {
    if (handle.raf !== null) cancelAnimationFrame(handle.raf);
    handle.raf = null;
  };
  handle.resume = resume;
  handle.cleanup = handle.suspend;
  resume();

  handles.set(container, handle);
}

/** Stops a cloud when its owning view is no longer active. Safe to call twice. */
export function disposeKeywordCloud(container: HTMLElement | null | undefined): void {
  if (!container) return;
  handles.get(container)?.cleanup();
  handles.delete(container);
}

/** Pauses a retained view's animation without discarding its rendered nodes. */
export function suspendKeywordCloud(container: HTMLElement | null | undefined): void {
  if (!container) return;
  handles.get(container)?.suspend();
}

/** Restarts a retained cloud at its last projected angle. Safe to call repeatedly. */
export function resumeKeywordCloud(container: HTMLElement | null | undefined): void {
  if (!container) return;
  handles.get(container)?.resume();
}

/**
 * Sphere-shaped loading placeholder: ghost pills placed with the same
 * Fibonacci-sphere math and rotating projection as the real cloud.
 * Replaces the old horizontal bar skeletons so the reserved keyword box
 * keeps a stable height AND a stable silhouette while keywords load.
 */
const SKELETON_WEIGHTS = [1, 0.42, 0.74, 0.3, 0.86, 0.55, 0.36, 0.66];
const SKELETON_COUNT = 14;
const SKELETON_SIGNATURE = "\u0000skeleton";

export function renderKeywordCloudSkeleton(container: HTMLElement): void {
  handles.get(container)?.cleanup();
  handles.delete(container);

  container.classList.remove("topic-keyword-cloud--static");
  container.classList.add("kw-cloud-3d", "kw-cloud--skeleton");

  const nodes: CloudNode[] = Array.from({ length: SKELETON_COUNT }, (_, index) => {
    const el = document.createElement("span");
    el.className = "topic-keyword kw-skeleton-pill";
    el.style.setProperty("--kw-weight", String(SKELETON_WEIGHTS[index % SKELETON_WEIGHTS.length]));
    el.style.setProperty("--kw-i", String(index));
    const y = 1 - ((index + 0.5) / SKELETON_COUNT) * 2;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = GOLDEN_ANGLE * index;
    return { el, x: Math.cos(theta) * r, y, z: Math.sin(theta) * r };
  });

  container.replaceChildren(...nodes.map((n) => n.el));

  const reduceMotion = typeof window !== "undefined"
    && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  let radius = computeRadius(container);
  let angleY = 0;
  let angleX = INITIAL_ANGLE_X;
  const project = () => {
    if (radius === 0) radius = computeRadius(container);
    projectNodes(nodes, radius, angleY, angleX);
  };

  project();

  const handle: CloudHandle = {
    raf: null,
    signature: SKELETON_SIGNATURE,
    firstEl: nodes[0]?.el ?? null,
    angleY,
    angleX,
    suspend: () => {},
    resume: () => {},
    cleanup: () => {},
  };

  const resume = () => {
    if (reduceMotion || handle.raf !== null) return;
    if (!container.isConnected || nodes[0]?.el.parentElement !== container) return;
    const tick = () => {
      if (!container.isConnected || nodes[0]?.el.parentElement !== container) {
        handle.raf = null;
        return;
      }
      angleY += ROTATE_Y_PER_FRAME;
      angleX += ROTATE_X_PER_FRAME;
      handle.angleY = angleY;
      handle.angleX = angleX;
      project();
      handle.raf = requestAnimationFrame(tick);
    };
    handle.raf = requestAnimationFrame(tick);
  };
  handle.suspend = () => {
    if (handle.raf !== null) cancelAnimationFrame(handle.raf);
    handle.raf = null;
  };
  handle.resume = resume;
  handle.cleanup = handle.suspend;
  resume();

  handles.set(container, handle);
}

function projectNodes(nodes: CloudNode[], radius: number, angleY: number, angleX: number): void {
  const cosY = Math.cos(angleY);
  const sinY = Math.sin(angleY);
  const cosX = Math.cos(angleX);
  const sinX = Math.sin(angleX);
  for (const node of nodes) {
    const rx = node.x * cosY - node.z * sinY;
    const rz = node.x * sinY + node.z * cosY;
    const ry = node.y * cosX - rz * sinX;
    const rz2 = node.y * sinX + rz * cosX;
    const depth = (rz2 + 1) / 2; // 0 (back) .. 1 (front)
    const px = rx * radius;
    const py = ry * radius;
    const scale = 0.62 + depth * 0.55;
    node.el.style.transform =
      `translate(-50%, -50%) translate3d(${px.toFixed(1)}px, ${py.toFixed(1)}px, 0) scale(${scale.toFixed(3)})`;
    node.el.style.opacity = (0.4 + depth * 0.6).toFixed(3);
    node.el.style.zIndex = String(Math.round(depth * 100));
  }
}

function computeRadius(container: HTMLElement): number {
  const w = container.clientWidth || 300;
  const h = container.clientHeight || 200;
  // Leave headroom so the widest words stay inside the box.
  return Math.max(60, Math.min(w, h) * 0.42);
}

function normalizeKeywords(keywords: KeywordItem[]): KeywordItem[] {
  const out: KeywordItem[] = [];
  for (const raw of keywords ?? []) {
    if (typeof raw?.keyword !== 'string' || raw.keyword.trim().length === 0 || typeof raw.coefficient !== 'number') continue;
    out.push({ keyword: raw.keyword, coefficient: raw.coefficient });
  }
  return out;
}
